/**
 * The user's accounts, one row each: name, signal, last order, sales for the
 * picked period (month or year to date) and the change vs. last year.
 * Filtered by the All | Assigned | Unassigned picker and the search box. Each row
 * opens that account's page.
 */

import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { SALES_INSIGHTS_ROUTE } from "@/hooks/use-sales-insights";
import {
  inScope,
  type BookScope,
  type SalesInsightsAccountListItem,
} from "@onsemble/shared/types/sales-insights";
import { count, dollars } from "../../format";
import { GrowthPill } from "../../shared/GrowthPill";
import { lastOrderText } from "../../shared/lastOrderText";
import type { Period } from "../../shared/PeriodTabs";
import { SignalTag } from "../../shared/SignalTag";

/**
 * The accounts in order for the period. Month: the server's order (signals
 * first, then most sales this month). Year: the same rule by sales this year.
 */
export function orderForPeriod(
  accounts: SalesInsightsAccountListItem[],
  period: Period,
): SalesInsightsAccountListItem[] {
  // The server already sends month order
  if (period === "month") return accounts;
  return [...accounts].sort((a, b) => {
    // A risk or opportunity sorts above steady
    const aSteady = a.signal.group === "steady" ? 1 : 0;
    const bSteady = b.signal.group === "steady" ? 1 : 0;
    if (aSteady !== bSteady) return aSteady - bSteady;
    // Then largest year-to-date sales
    if (a.salesYtd.sales !== b.salesYtd.sales) return b.salesYtd.sales - a.salesYtd.sales;
    // Then alphabetical, so ties always come out the same way
    return a.name.localeCompare(b.name);
  });
}

/** Accounts in the picked choice, and names containing the search text. */
export function visibleAccounts(
  accounts: SalesInsightsAccountListItem[],
  scope: BookScope,
  query: string,
): SalesInsightsAccountListItem[] {
  // Ignore case and surrounding spaces
  const q = query.trim().toLowerCase();
  return accounts.filter(
    (a) =>
      // Only accounts the picked choice covers
      inScope(scope, a.assignment) &&
      // Any part of the name; an empty search matches everything
      (!q || a.name.toLowerCase().includes(q)),
  );
}

// Column widths: name and signal, sales for the period, vs. last year
const COLUMNS = "grid grid-cols-[minmax(0,1fr)_120px_76px] gap-4";

// Underlined text button, for the ways out of "no matches"
const LINK_BUTTON =
  "inline-flex min-h-11 items-center px-1 text-si-sm font-semibold text-si-ink-2 underline underline-offset-[3px] hover:text-si-ink";

export function AccountList({
  accounts,
  scope,
  scopes,
  query,
  period,
  onShowAll,
  onClearQuery,
}: {
  /** The whole book, in the server's order */
  accounts: SalesInsightsAccountListItem[];
  /** The picked All | Assigned | Unassigned choice */
  scope: BookScope;
  /** The choices this user has */
  scopes: BookScope[];
  query: string;
  /** Month to date or year to date: picks the sales, change and order */
  period: Period;
  /** Switch the picker to All, from the "no matches" message */
  onShowAll: () => void;
  /** Empty the search box, from the "no matches" message */
  onClearQuery: () => void;
}) {
  const { t } = useTranslation();
  const year = period === "year";
  // What's shown, after the picker and the search, in the period's order
  const rows = orderForPeriod(visibleAccounts(accounts, scope, query), period);
  // What the picker allows, before the search narrows it
  const pool = visibleAccounts(accounts, scope, "");

  return (
    <>
      {/* Column headers; screen readers skip them and read each row's own text */}
      <div
        aria-hidden="true"
        className={`${COLUMNS} border-b border-si-line px-1 py-2 text-si-sm font-medium text-si-ink-2`}
      >
        <span>{t("pages.pcInsights.list.accountColumn")}</span>
        <span className="text-right">
          {t(year ? "pages.pcInsights.list.ytdColumn" : "pages.pcInsights.list.mtdColumn")}
        </span>
        <span className="text-right">{t("pages.pcInsights.list.vsLastYearColumn")}</span>
      </div>

      <ul className="m-0 list-none p-0">
        {rows.map((a) => (
          <li key={a.accountId}>
            {/* The whole row opens the account's page */}
            <Link
              href={`${SALES_INSIGHTS_ROUTE}/accounts/${encodeURIComponent(a.accountId)}`}
              className={`${COLUMNS} items-center border-b border-si-line px-1 py-4 text-si-ink no-underline transition-colors hover:bg-si-surface active:bg-si-surface-2 focus-visible:rounded-si-sm focus-visible:outline-offset-[-2px]`}
            >
              {/* Name, then signal · last order, and a tag on house accounts */}
              <div>
                <div className="text-si-md font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]">
                  {a.name}
                </div>
                <div className="mt-0.5 text-si-sm text-si-ink-3">
                  <SignalTag signal={a.signal} /> · {lastOrderText(t, a.lastOrderDaysAgo, "list")}
                  {a.assignment === "house" && (
                    <span className="ml-1 inline-block rounded-full border border-si-line-strong px-[7px] align-[1px] text-si-xs font-medium leading-[18px] text-si-ink-2">
                      {t("pages.pcInsights.list.houseTag")}
                    </span>
                  )}
                </div>
              </div>
              {/* Sales for the period, full dollars */}
              <div className="text-right text-si-md font-medium tabular-nums">
                {dollars((year ? a.salesYtd : a.salesMtd).sales)}
              </div>
              {/* Change vs. the same days last year */}
              <GrowthPill growth={(year ? a.salesYtd : a.salesMtd).growth} />
            </Link>
          </li>
        ))}
      </ul>

      {rows.length === 0 && (
        // Nothing matches the search: say so, and offer ways out
        <div className="flex flex-wrap items-center gap-2 border-b border-si-line px-1 py-8 text-si-ink-2">
          {t("pages.pcInsights.list.noMatch", { query: query.trim() })}
          {/* Only offered when the user has All and isn't on it */}
          {scopes.includes("all") && scope !== "all" && (
            <button type="button" onClick={onShowAll} className={LINK_BUTTON}>
              {t("pages.pcInsights.controls.showAll")}
            </button>
          )}
          <button type="button" onClick={onClearQuery} className={LINK_BUTTON}>
            {t("pages.pcInsights.list.clearSearch")}
          </button>
        </div>
      )}

      {/* How many are shown, and how they're ordered */}
      <p className="mt-4 text-si-md text-si-ink-2">
        {rows.length === pool.length
          ? t("pages.pcInsights.list.showingAll", { total: count(pool.length) })
          : t("pages.pcInsights.list.showingSome", {
              shown: count(rows.length),
              total: count(pool.length),
            })}
      </p>
    </>
  );
}
